import {useEffect, useRef, useState} from 'react';

import {useThemeColors} from '@nfq/colors';

/**
 * A custom hook for managing hover-based tooltips.
 * This hook provides functionality to show and position a tooltip when the user hovers over an element.
 * It ensures proper positioning within the viewport and introduces a slight delay before showing the tooltip content.
 *
 * @returns An object containing:
 * - `colors`: The theme colors used for styling the tooltip.
 * - `handleMouseEnter`: A function to trigger the tooltip visibility.
 * - `handleMouseLeave`: A function to hide the tooltip.
 * - `isTooltipVisible`: A boolean indicating whether the tooltip is currently visible.
 * - `showTooltipContent`: A boolean indicating whether the tooltip content should be displayed (delayed appearance).
 * - `tooltipRef`: A ref for the tooltip element.
 * - `tooltipWrapperRef`: A ref for the element that triggers the tooltip.
 *
 * @example
 * ```tsx
 * const {
 *     handleMouseEnter,
 *     handleMouseLeave,
 *     isTooltipVisible,
 *     showTooltipContent,
 *     tooltipRef,
 *     tooltipWrapperRef
 * } = useHoverTooltip();
 *
 * return (
 *     <div ref={tooltipWrapperRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
 *         Hover over me
 *         {isTooltipVisible && (
 *             <div ref={tooltipRef} className="tooltip">
 *                 {showTooltipContent && "Tooltip Content"}
 *             </div>
 *         )}
 *     </div>
 * );
 * ```
 */
export const useHoverTooltip = () => {
    const colors = useThemeColors();
    const [isTooltipVisible, setTooltipVisible] = useState(false);
    const [showTooltipContent, setShowTooltipContent] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const tooltipWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const toolTipWrapper = tooltipWrapperRef.current;
        let timeoutId: NodeJS.Timeout;

        if (isTooltipVisible) {
            timeoutId = setTimeout(() => {
                setShowTooltipContent(true);
            }, 200);
        }

        toolTipWrapper?.addEventListener('mousemove', handleMouseMove, {
            capture: true,
            passive: true
        });

        return () => {
            toolTipWrapper?.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(timeoutId);
        };
    }, [isTooltipVisible, tooltipWrapperRef]);

    /**
     * Handles mouse movement to dynamically position the tooltip within the viewport.
     * This function ensures the tooltip follows the cursor while staying within screen boundaries.
     *
     * @param event The mouse event containing the cursor position.
     */
    const handleMouseMove = (event: MouseEvent) => {
        const {clientX, clientY} = event;
        const tooltipWidth = tooltipRef.current?.offsetWidth ?? 0;
        const tooltipHeight = tooltipRef.current?.offsetHeight ?? 0;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // eslint-disable-next-line @nfq/no-magic-numbers
        let tooltipX = clientX - (tooltipWidth / 2);
        // eslint-disable-next-line @nfq/no-magic-numbers
        let tooltipY = clientY + 18;

        if (tooltipX + tooltipWidth > viewportWidth) {
            // eslint-disable-next-line @nfq/no-magic-numbers
            tooltipX = clientX - tooltipWidth - 10;
        }

        if (tooltipY + tooltipHeight > viewportHeight) {
            // eslint-disable-next-line @nfq/no-magic-numbers
            tooltipY = viewportHeight - tooltipHeight - 10;
        }

        tooltipRef.current?.style.setProperty('left', `${tooltipX}px`);
        tooltipRef.current?.style.setProperty('top', `${tooltipY}px`);
    };

    /**
     * Handles the mouse enter event to display the tooltip.
     * This function sets the tooltip visibility to `true` while ensuring
     * that the tooltip content is initially hidden to allow for a delayed appearance.
     */
    const handleMouseEnter = () => {
        setTooltipVisible(true);
        setShowTooltipContent(false);
    };

    /**
     * Handles the mouse leave event to hide the tooltip.
     * This function sets the tooltip visibility to `false`, ensuring that the tooltip disappears when the mouse moves away.
     */
    const handleMouseLeave = () => {
        setTooltipVisible(false);
    };

    return {
        colors,
        handleMouseEnter,
        handleMouseLeave,
        isTooltipVisible,
        showTooltipContent,
        tooltipRef,
        tooltipWrapperRef
    };
};